var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { extendObservable, has as observableHas } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { FormGroup as BSFormGroup, HelpBlock as BSHelpBlock, ControlLabel, FormControl, InputGroup, } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { Descriptor } from "./Descriptor";
import { useFormContext } from "./Form";
import { useTranslation } from "./hooks/useTranslation";
import { useUtilityStyles } from "./hooks/useUtilityStyles";
import { classNames } from "./utils/classNames";
import { getIsOverMinFn, getIsUnderMaxFn, isEmail, isInteger, isNumber, isURL, } from "./utils/validators";
export var DEFAULT_ID = uuidv4();
export var ensureObservableValue = function (obj, key) {
    var _a;
    // Since key can be e.g. learners[0].firstName or learner.firstName
    // we want to make sure we're only extending the observable with the top-level key e.g. learners/learner
    var topLevelKey = key.split(/[[.]/)[0];
    if (!observableHas(obj, topLevelKey)) {
        extendObservable(obj, (_a = {},
            _a[topLevelKey] = null,
            _a));
    }
    if (obj[topLevelKey] === undefined) {
        obj[topLevelKey] = null;
    }
};
var ERROR_ID_COUNTER = 0;
export function withFormWrapper(_a) {
    var WrappedComponent = _a.WrappedComponent, options = _a.options;
    var LabeledInput = observer(function (_a) {
        var valid = _a.valid, required = _a.required, prepend = _a.prepend, helpMessage = _a.helpMessage, name = _a.name, type = _a.type, min = _a.min, max = _a.max, labelRemoved = _a.labelRemoved, props = __rest(_a, ["valid", "required", "prepend", "helpMessage", "name", "type", "min", "max", "labelRemoved"]);
        var utilities = useUtilityStyles(props === null || props === void 0 ? void 0 : props.styleOptions).utilities;
        var onComponentChange = props.onChange;
        if (name === undefined) {
            throw new Error("Input does not have a name");
        }
        var errorId = React.useState(function () { return "".concat(ERROR_ID_COUNTER++); })[0];
        var _b = React.useState(false), isRawValueInvalid = _b[0], setisRawValueInvalid = _b[1];
        var _c = useFormContext(), values = _c.values, errors = _c.errors, setIsDirty = _c.setIsDirty, isStatic = _c.isStatic;
        var t = useTranslation();
        var isURLWithErrorMsg = function (value) {
            return valueIsBlank(value) || isURL(value) || t("Value must be a valid URL");
        };
        var isEmailWithErrorMsg = function (value) {
            return valueIsBlank(value) ||
                isEmail(value) ||
                t("Value must be a valid email");
        };
        var isNumberWithErrorMsg = function (value) {
            return valueIsBlank(value) ||
                isNumber(value) ||
                t("Value must be a valid number");
        };
        var isIntegerWithErrorMsg = function (value) {
            return valueIsBlank(value) ||
                isInteger(value) ||
                t("Value must be a valid integer");
        };
        var isOverMinWithErrorMsg = function (minValue) { return function (value) {
            var isOverMin = getIsOverMinFn(minValue);
            return (valueIsBlank(value) ||
                isOverMin(value) ||
                "".concat(t("Value must be above minimum of"), " ").concat(minValue));
        }; };
        var isUnderMaxWithErrorMsg = function (maxValue) { return function (value) {
            var isUnderMax = getIsUnderMaxFn(maxValue);
            return (valueIsBlank(value) ||
                isUnderMax(value) ||
                "".concat(t("Value must be below maximum of"), " ").concat(maxValue));
        }; };
        var isDateTimeWithErrorMsg = useCallback(function () {
            if (isRawValueInvalid) {
                if (type === "time") {
                    return t("Invalid Time");
                }
                if (type === "datetime") {
                    return t("Invalid Datetime");
                }
            }
            return true;
        }, [isRawValueInvalid, type]);
        React.useEffect(function () {
            ensureObservableValue(values, name);
            ensureObservableValue(errors, errorId);
        }, [values, errors, name, errorId]);
        var getRequiredValidator = React.useMemo(function () {
            return function (value) {
                if (typeof value === "object" &&
                    value !== null &&
                    options === "objectValidation") {
                    Object.keys(value).forEach(function (key) {
                        if (valueIsBlank(value[key])) {
                            if (typeof required === "string") {
                                return required;
                            }
                            return t("Required");
                        }
                    });
                    return true;
                }
                if (valueIsBlank(value)) {
                    if (typeof required === "string") {
                        return required;
                    }
                    return t("Required");
                }
                return true;
            };
        }, [required]);
        var validators = React.useMemo(function () {
            var validFns = [];
            if (required)
                validFns.push(getRequiredValidator);
            if (valid)
                validFns.push(valid);
            switch (type) {
                case "email":
                    validFns.push(isEmailWithErrorMsg);
                    break;
                case "url":
                    validFns.push(isURLWithErrorMsg);
                    break;
                case "number":
                    validFns.push(isNumberWithErrorMsg);
                    break;
                case "integer":
                    validFns.push(isIntegerWithErrorMsg);
                    break;
                case "time":
                    validFns.push(isDateTimeWithErrorMsg);
                    break;
                case "datetime":
                    validFns.push(isDateTimeWithErrorMsg);
                    break;
            }
            if (min)
                validFns.push(isOverMinWithErrorMsg(min));
            if (max)
                validFns.push(isUnderMaxWithErrorMsg(max));
            return validFns;
        }, [type, required, valid, min, max, isDateTimeWithErrorMsg]);
        var handleInputStatusChange = useCallback(function (isInvalid) {
            setisRawValueInvalid(isInvalid);
        }, []);
        // this should not be needed, but the typing is being problematic
        var Component = WrappedComponent;
        var _d = validateInput(values.get(name), validators, options === "objectValidation"), validationState = _d.validationState, errorMessage = _d.errorMessage;
        React.useEffect(function () {
            if (errors[errorId] !== validationState) {
                errors[errorId] = validationState;
            }
        }, [errors, errorId, validationState]);
        // clear error state when input is unmounted
        var onUnmount = React.useState(function () { return function () {
            if (errors[errorId] !== null) {
                errors[errorId] = null;
            }
        }; })[0];
        React.useEffect(function () { return onUnmount; }, [onUnmount]);
        var onChange = function (newValue, oldValue, newLabel, oldLabel) {
            setIsDirty(true);
            if (onComponentChange) {
                onComponentChange(newValue, oldValue, newLabel, oldLabel);
            }
        };
        var inputType = type === "integer" ? "number" : type;
        if ((!props.forceAlwaysLive && isStatic) || options === "validationOnly")
            return (React.createElement(Component, __assign({ name: name, type: inputType, min: min, max: max }, props)));
        if (prepend) {
            return (React.createElement(HorizontalFormGroup, { uniqueId: props.uniqueId || DEFAULT_ID, label: props.label, name: name, prepend: prepend, validationState: validationState, dataId: props.dataId, dataLabel: props.dataLabel, dataTestId: props.dataTestId, className: utilities },
                React.createElement(Component, __assign({ name: name, type: inputType, min: min, max: max }, props, { onChange: onChange, onInputStatusChange: handleInputStatusChange }))));
        }
        return (React.createElement(FormGroup, { className: utilities, validationState: validationState, name: name, labelHidden: props.labelHidden, options: options, uniqueId: props.uniqueId ? "".concat(props.uniqueId) : DEFAULT_ID, describingId: props === null || props === void 0 ? void 0 : props.describingId, dataId: props.dataId, dataLabel: props.dataLabel, dataTestId: props.dataTestId },
            React.createElement("div", { className: classNames(helpMessage
                    ? "form-group-top-wrapper--with-helpmessage"
                    : "form-group-top-wrapper", { "label-hidden": !!props.labelHidden }) },
                (options === "group" || options === "omitHTMLLabel") && (React.createElement("span", { className: "form-control-static-label", id: "".concat(name, "-title-").concat(props.uniqueId || DEFAULT_ID) }, props.label)),
                !labelRemoved &&
                    options !== "omitHTMLLabel" &&
                    options !== "group" &&
                    props.label ? (React.createElement(ControlLabel, { id: props === null || props === void 0 ? void 0 : props.describingId }, props.label)) : null,
                options !== "checkbox" && errorMessage ? (React.createElement(BSHelpBlock, { className: "pull-right", style: { margin: 0 }, "data-hook": "required-warning" }, errorMessage)) : null,
                helpMessage && (React.createElement("div", { className: "pull-right helpmessage-wrapper" },
                    React.createElement(Descriptor, { message: helpMessage, placement: "top" })))),
            React.createElement("div", { style: !labelRemoved ? { position: "relative" } : {} },
                React.createElement(Component, __assign({ name: name, type: inputType, min: min, max: max }, props, { onChange: onChange, onInputStatusChange: handleInputStatusChange })),
                React.createElement(FormControl.Feedback, null),
                options === "checkbox" && (React.createElement(BSHelpBlock, { style: { fontSize: 12 }, "data-hook": "required-warning" }, errorMessage)))));
    });
    return LabeledInput;
}
var FormGroup = function (_a) {
    var children = _a.children, validationState = _a.validationState, name = _a.name, options = _a.options, labelHidden = _a.labelHidden, uniqueId = _a.uniqueId, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId, className = _a.className, describingId = _a.describingId;
    var controlId = describingId === undefined ? "form-group-".concat(name, "-").concat(uniqueId) : undefined;
    if (options === "group") {
        return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, controlId: controlId, role: "group", "aria-labelledby": "".concat(name, "-title-").concat(uniqueId), className: classNames(labelHidden ? "label-hidden" : "", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId }, children));
    }
    return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, controlId: controlId, className: classNames(labelHidden ? "label-hidden" : "", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId }, children));
};
var HorizontalFormGroup = function (_a) {
    var children = _a.children, uniqueId = _a.uniqueId, label = _a.label, prepend = _a.prepend, name = _a.name, validationState = _a.validationState, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId, className = _a.className;
    return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, className: classNames("horizontal-form-group", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId },
        React.createElement(InputGroup, null,
            prepend && (React.createElement("label", { className: "input-group-addon", htmlFor: "form-group-".concat(name, "-").concat(uniqueId) }, label)),
            children,
            React.createElement(FormControl.Feedback, null))));
};
function validateInput(value, validators, objectValidation) {
    var errorMessage;
    var validationState = null;
    if (typeof value === "object" && value !== null && objectValidation) {
        Object.keys(value).forEach(function (key) {
            for (var _i = 0, validators_2 = validators; _i < validators_2.length; _i++) {
                var validator = validators_2[_i];
                var validationResult = validator(value[key]);
                if (validationResult !== true) {
                    validationState = "error";
                    if (typeof validationResult === "string") {
                        errorMessage = validationResult;
                    }
                }
            }
        });
    }
    for (var _i = 0, validators_1 = validators; _i < validators_1.length; _i++) {
        var validator = validators_1[_i];
        var validationResult = validator(value);
        if (validationResult !== true) {
            validationState = "error";
            if (typeof validationResult === "string") {
                errorMessage = validationResult;
            }
            return { validationState: validationState, errorMessage: errorMessage };
        }
    }
    return { validationState: validationState, errorMessage: errorMessage };
}
function valueIsBlank(value) {
    return (value === undefined ||
        value === null ||
        value === "" ||
        value === false ||
        value.length === 0);
}
export var emptyStringIfNull = function (value) {
    return value === null || value === undefined ? "" : value;
};
export var HelpBlock = function (props) {
    var classes = useUtilityStyles(props === null || props === void 0 ? void 0 : props.styleOptions);
    return (React.createElement(BSHelpBlock, { className: classNames("pull-right", classes.utilities) }, props.message));
};
